import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Switch,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  InputNumber,
  Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../utils/api';
// 添加 react-quill 导入
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // 引入样式
import ImageUpload from '../../components/ImageUpload';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { confirm } = Modal;

const NewsList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [newsList, setNewsList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});


  // 获取资讯列表
  const fetchNewsList = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      };

      const response = await api.get('/admin/news/list', params);
      if (response.code === 200) {
        setNewsList(response.data.list);
        setPagination({
          ...pagination,
          total: response.data.total,
        });
      } else {
        message.error(response.message || '获取资讯列表失败');
      }
    } catch (error) {
      message.error('获取资讯列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsList();
  }, [pagination.current, pagination.pageSize, filters]);

  // 处理表格分页变化
  const handleTableChange = (pagination, filters, sorter) => {
    setPagination({
      ...pagination,
      current: pagination.current,
    });
  };

  // 处理搜索
  const handleSearch = (values) => {
    const newFilters = {};
    if (values.keyword) {
      newFilters.keyword = values.keyword;
    }
    // 确保状态筛选正确处理
    if (values.status !== undefined && values.status !== '' && values.status !== null) {
      newFilters.status = values.status;
    }
    
    setFilters(newFilters);
    setPagination({
      ...pagination,
      current: 1,
    });
  };

  // 重置搜索
  const handleReset = () => {
    setFilters({});
    setPagination({
      ...pagination,
      current: 1,
    });
    // 重置表单
    form.resetFields();
  };

  // 跳转到新增页面
  const goToCreate = () => {
    navigate('/news/create');
  };

  // 跳转到编辑页面
  const goToEdit = (id) => {
    navigate(`/news/edit/${id}`);
  };

  // 删除资讯
  const handleDelete = (news) => {
    confirm({
      title: '确认删除',
      content: `确定要删除资讯"${news.title}"吗？`,
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/news/${news.id}`);
          if (response.code === 200) {
            message.success('删除成功');
            fetchNewsList();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败: ' + error.message);
        }
      },
    });
  };

  // 更新状态
  const updateStatus = async (news, status) => {
    try {
      const response = await api.put(`/admin/news/${news.id}/status`, { status });
      if (response.code === 200) {
        message.success('状态更新成功');
        fetchNewsList();
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败: ' + error.message);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 120,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => {
        // 确保 tags 是数组
        const tagList = Array.isArray(tags) ? tags : 
                      (typeof tags === 'string' ? [tags] : 
                      (tags && typeof tags === 'object' ? Object.values(tags) : []));
        
        return (
          <>
            {tagList.map(tag => (
              <Tag key={tag} color="blue">{tag}</Tag>
            ))}
          </>
        );
      },
    },
    {
      title: '发布时间',
      dataIndex: 'publishTime',
      key: 'publishTime',
      width: 180,
      render: (publishTime) => publishTime ? dayjs(publishTime).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => updateStatus(record, checked ? 1 : 0)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => goToEdit(record.id)}
            size="small"
          >
            编辑
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="资讯管理"
      extra={
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={goToCreate}
        >
          新增资讯
        </Button>
      }
    >
      {/* 搜索表单 */}
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        initialValues={{ status: '' }}
        style={{ marginBottom: 16 }}
      >
        <Form.Item name="keyword" label="关键词">
          <Input placeholder="标题/作者" />
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select style={{ width: 120 }}>
            <Option value="">全部</Option>
            <Option value="1">启用</Option>
            <Option value="0">禁用</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 资讯列表 */}
      <Table
        columns={columns}
        dataSource={newsList}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        onChange={handleTableChange}
        rowKey="id"
      />


    </Card>
  );
};

export default NewsList;